import { z } from 'zod';

/**
 * Forma normalizada do catálogo, montada pela ingestão a partir de três
 * chamadas da Catalog API. É a única fonte de verdade para grounding das
 * sugestões e para rollback de escrita — nada é enriquecido a partir de dado
 * que não esteja aqui.
 */

export const snapshotImageSchema = z.object({
  fileId: z.number().int(),
  /** Alt text real da imagem. `label` é só tag interna de agrupamento. */
  text: z.string().nullable(),
  label: z.string().nullable(),
  name: z.string().nullable(),
  url: z.string(),
  isMain: z.boolean(),
});
export type SnapshotImage = z.infer<typeof snapshotImageSchema>;

export const snapshotSpecificationSchema = z.object({
  fieldId: z.number().int().nullable(),
  name: z.string(),
  values: z.array(z.string()),
});
export type SnapshotSpecification = z.infer<typeof snapshotSpecificationSchema>;

export const snapshotMeasuresSchema = z.object({
  cubicWeight: z.number().nullable(),
  height: z.number().nullable(),
  length: z.number().nullable(),
  weightKg: z.number().nullable(),
  width: z.number().nullable(),
});
export type SnapshotMeasures = z.infer<typeof snapshotMeasuresSchema>;

export const snapshotSkuSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  nameComplete: z.string().nullable(),
  isActive: z.boolean(),
  ean: z.string().nullable(),
  refId: z.string().nullable(),
  manufacturerCode: z.string().nullable(),
  measures: snapshotMeasuresSchema,
  images: z.array(snapshotImageSchema),
  specifications: z.array(snapshotSpecificationSchema),
});
export type SnapshotSku = z.infer<typeof snapshotSkuSchema>;

export const snapshotProductSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  /** Vem só do GET /api/catalog/pvt/product/{id}. */
  title: z.string().nullable(),
  description: z.string().nullable(),
  descriptionShort: z.string().nullable(),
  /** Meta description. Também exclusiva do endpoint de produto. */
  metaTagDescription: z.string().nullable(),
  keywords: z.string().nullable(),
  /** Slug da URL. Fora de escopo de escrita por padrão. */
  linkId: z.string().nullable(),
  refId: z.string().nullable(),
  brandId: z.number().int().nullable(),
  brandName: z.string().nullable(),
  categoryId: z.number().int().nullable(),
  departmentId: z.number().int().nullable(),
  isActive: z.boolean(),
  isVisible: z.boolean(),
  showWithoutStock: z.boolean(),
  releaseDate: z.string().nullable(),
});
export type SnapshotProduct = z.infer<typeof snapshotProductSchema>;

/** Valor permitido de Combo/Radio (FieldValues VTEX). */
export const categorySpecAllowedValueSchema = z.object({
  id: z.number().int(),
  value: z.string(),
});
export type CategorySpecAllowedValue = z.infer<typeof categorySpecAllowedValueSchema>;

/** Definição de campo de especificação declarada na categoria. */
export const categorySpecFieldSchema = z.object({
  fieldId: z.number().int(),
  name: z.string(),
  isActive: z.boolean(),
  isRequired: z.boolean(),
  isFilter: z.boolean(),
  isOnProductDetails: z.boolean(),
  isStockKeepingUnit: z.boolean(),
  fieldTypeName: z.string().nullable(),
  /** FieldTypeId VTEX — fallback quando FieldTypeName vem nulo. */
  fieldTypeId: z.number().int().nullable().default(null),
  /** Lista fechada para Combo/Radio; Text fica vazio. */
  allowedValues: z.array(categorySpecAllowedValueSchema).default([]),
});
export type CategorySpecField = z.infer<typeof categorySpecFieldSchema>;

export const productSnapshotDataSchema = z.object({
  product: snapshotProductSchema,
  skus: z.array(snapshotSkuSchema),
  /** Specs em nível de produto, já resolvidas em nome + valores. */
  specifications: z.array(snapshotSpecificationSchema),
  categoryId: z.number().int().nullable(),
  categoryName: z.string().nullable(),
  /** Do departamento até a folha, ex.: ["Moda", "Feminino", "Vestidos"]. */
  categoryPath: z.array(z.string()),
  /** Campos declarados na categoria, para detectar specs obrigatórias faltando. */
  categorySpecFields: z.array(categorySpecFieldSchema),
});
export type ProductSnapshotData = z.infer<typeof productSnapshotDataSchema>;

/**
 * Recorte do snapshot que pode ser mostrado ao LLM: apenas atributos
 * estruturados verificados. Descrição e meta description atuais ficam de fora
 * de propósito — parafrasear o texto existente é exatamente o que a política
 * de spam do Google trata como scaled content abuse.
 */
export interface GroundingFacts {
  productId: number;
  name: string;
  brandName: string | null;
  categoryName: string | null;
  categoryPath: string[];
  refId: string | null;
  specifications: Array<{ name: string; values: string[] }>;
  skus: Array<{
    id: number;
    name: string;
    ean: string | null;
    measures: SnapshotMeasures;
    specifications: Array<{ name: string; values: string[] }>;
  }>;
}

export function extractGroundingFacts(snapshot: ProductSnapshotData): GroundingFacts {
  return {
    productId: snapshot.product.id,
    name: snapshot.product.name,
    brandName: snapshot.product.brandName,
    categoryName: snapshot.categoryName,
    categoryPath: snapshot.categoryPath,
    refId: snapshot.product.refId,
    specifications: snapshot.specifications.map((s) => ({ name: s.name, values: s.values })),
    skus: snapshot.skus.map((sku) => ({
      id: sku.id,
      name: sku.name,
      ean: sku.ean,
      measures: sku.measures,
      specifications: sku.specifications.map((s) => ({ name: s.name, values: s.values })),
    })),
  };
}
